import React, { useState } from 'react';
import { Box, Button, CardContent, Divider, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FormattedMessage } from 'react-intl';
import { dispatch } from 'store';
import {
    CustomFilter,
    GenericTableDateFilterFormat,
    GenericTableFilterType,
    GenericTableFilterView,
} from 'inside-fifa/api/generic-tables/dto';
import DateFilter from '../date-filter';
import SelectFilter from '../select-filter';
import { FilterProps, LocalizedLabel } from './dto';

const Filter: React.FC<FilterProps> = ({
    onSaveOrEdit,
    onCancel,
    filterData,
    columns,
    dateFormats,
    viewFormats,
    filterTypes,
    languagesUsed,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedFilterKey, setSelectedFilterKey] = useState<GenericTableFilterType | undefined>(filterData?.type);
    const [isShowOutsideChecked, setShowOutsideChecked] = useState<boolean | undefined>(
        filterData?.shouldShowOutsidePanel,
    );
    const [isHideOptionAll, setHideOptionAll] = useState<boolean | undefined>(filterData?.hideOptionAll);
    const [selectedDataColumn, setSelectedDataColumn] = useState<string | undefined>(filterData?.dataColumn);
    const [selectedViewKey, setSelectedViewKey] = useState<GenericTableFilterView | undefined>(filterData?.view);
    const [selectedDateTypeKey, setSelectedDateTypeKey] = useState<GenericTableDateFilterFormat | undefined>(
        filterData?.format,
    );
    const [label, setLabel] = useState<LocalizedLabel>(filterData?.label ? filterData?.label : {});
    const [allOptionsLabel, setAllOptionsLabel] = useState<LocalizedLabel>(
        filterData?.allOptionsLabel ? filterData?.allOptionsLabel : {},
    );

    const renderSelectedFilter = () => {
        switch (selectedFilterKey) {
            case GenericTableFilterType.DateFilter:
                return (
                    <DateFilter
                        dateFormats={dateFormats}
                        selectedDataColumn={selectedDataColumn}
                        selectedDateTypeKey={selectedDateTypeKey}
                        isShowOutsideChecked={isShowOutsideChecked}
                        isShowHideOptionAllSelector = {false}
                        isHideOptionAll={isHideOptionAll}
                        dataColumns={columns}
                        setSelectedDataColumn={(eventValue) => setSelectedDataColumn(eventValue)}
                        setSelectedDateTypeKey={(eventValue) => setSelectedDateTypeKey(eventValue)}
                        setShowOutsideChecked={(eventValue) => setShowOutsideChecked(eventValue)}
                        setHideOptionAll={(eventValue) => setHideOptionAll(eventValue)}
                        languages={languagesUsed}
                        label={label}
                        setLabel={(e: LocalizedLabel) => setLabel(e)}
                    />
                );
            case GenericTableFilterType.MultipleSelectFilter:
            case GenericTableFilterType.SingleSelectFilter:
                return (
                    <SelectFilter
                        selectedDataColumn={selectedDataColumn}
                        selectedViewKey={selectedViewKey}
                        isShowOutsideChecked={isShowOutsideChecked}
                        isHideOptionAll={isHideOptionAll}
                        dataColumns={columns}
                        isShowHideOptionAllSelector = {true}
                        setSelectedDataColumn={(eventValue) => setSelectedDataColumn(eventValue)}
                        setSelectedViewKey={(eventValue) => setSelectedViewKey(eventValue)}
                        setShowOutsideChecked={(eventValue) => setShowOutsideChecked(eventValue)}
                        setHideOptionAll={(eventValue) => setHideOptionAll(eventValue)}
                        viewFormats={viewFormats}
                        languages={languagesUsed}
                        label={label}
                        setLabel={(e: LocalizedLabel) => setLabel(e)}
                        allOptionsLabel={allOptionsLabel}
                        setAllOptionsLabel={(e: LocalizedLabel) => setAllOptionsLabel(e)}
                    />
                );
            default:
                return null; // Or provide a default component or message if needed
        }
    };

    const handleCancel = () => {
        setIsLoading(true);
        dispatch(() => {
            onCancel();
            setIsLoading(false);
        });
    };

    const handleSave = () => {
        setIsLoading(true);
        const customFilter: CustomFilter = {
            type: selectedFilterKey!,
            dataColumn: selectedDataColumn,
            format: selectedDateTypeKey,
            view: selectedViewKey,
            shouldShowOutsidePanel: isShowOutsideChecked!,
            label: label,
            allOptionsLabel: allOptionsLabel,
            hideOptionAll: isHideOptionAll!,
        };
        dispatch(() => {
            onSaveOrEdit(customFilter);
            setIsLoading(false);
        });
    };

    function onChangeFilterType(filterType: GenericTableFilterType): void {
        setSelectedFilterKey(filterType);
        setHideOptionAll(false);
    }

    return (
        <CardContent>
            <Stack direction="column" sx={{ px: 2.5, py: 2 }}>
                <Typography variant="h6">
                    <FormattedMessage id="inside-fifa.generic-tables.filters.update-table-filter" />
                </Typography>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }} display="grid">
                    <InputLabel>
                        <FormattedMessage id="inside-fifa.generic-tables.filters.filter-type" />
                    </InputLabel>
                    <Select
                        aria-label="filter type"
                        fullWidth
                        value={selectedFilterKey}
                        onChange={(e) => onChangeFilterType(e.target.value as GenericTableFilterType)}
                    >
                        {filterTypes.map((filterType) => (
                            <MenuItem key={filterType.key} value={filterType.key}>
                                {filterType.placeholder}
                            </MenuItem>
                        ))}
                    </Select>
                    {selectedFilterKey && <>{renderSelectedFilter()}</>}
                </Box>
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{ px: 2.5, py: 2 }}>
                <Button color="error" aria-label="cancel" size="small" disabled={isLoading} onClick={handleCancel}>
                    <FormattedMessage id="inside-fifa.cancel" />
                </Button>
                <LoadingButton
                    aria-label="save"
                    type="submit"
                    variant="contained"
                    size="small"
                    loading={isLoading}
                    onClick={handleSave}
                >
                    <FormattedMessage id="inside-fifa.save" />
                </LoadingButton>
            </Stack>
        </CardContent>
    );
};

export default Filter;
